import { getAssetUrl } from '@lighthouse/assets'
import { getCachedItem, loadScript, setCachedItem } from '@lighthouse/tools'
import { add, subtract } from 'rambda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'

import * as SC from './styles'
import type { CaptchaConfig, CaptchaInstance, CaptchaResult, VerificationCodeBtnProps, VerificationCodeBtnState } from './types'

export { type CaptchaResult } from './types'

declare global {
    interface Window {
        captchaInstance?: CaptchaInstance
        initAlicom4?: (config: CaptchaConfig, callback: (captcha: CaptchaInstance) => void) => void
    }
}

/**
 *
 *
 * @param {*} { getCodeHandle = () => {}, phoneRef }
 * @return {*}
 */

export const VerificationCodeBtn: React.FC<VerificationCodeBtnProps> = ({
    onGetCodeHandle,
    loading = false,
    style
}: VerificationCodeBtnProps) => {
    const currentTime = String(Date.now()).slice(0, 10)

    const [scriptLoaded, setScriptLoaded] = useState(false)

    // 动态加载阿里云图形验证脚本
    useEffect(() => {
        const loadCaptcha4Script = async () => {
            if (typeof window.initAlicom4 === 'function') {
                setScriptLoaded(true)
                return
            }

            try {
                const scriptUrl = getAssetUrl('js', 'ct4.js')
                await loadScript(scriptUrl)
                setScriptLoaded(true)
            } catch {
                // Failed to load captcha4 script
                console.warn?.('脚本加载失败')
            }
        }

        loadCaptcha4Script()
    }, [])

    const [state, setState] = useImmer<VerificationCodeBtnState>({
        disable: false,
        prevTime: Number(getCachedItem('lastTime', 0)),
        count: 60,
        codeStep: 0,
        codeText: '获取验证码'
    })

    const { disable, prevTime, count, codeStep, codeText } = state

    useEffect(() => {
        const lastCount = add(60, subtract(Number(prevTime), Number(currentTime)))
        if (codeStep === 1) {
            setState(draft => {
                draft.count = 60
                draft.codeText = `${String(60)} s后重发`
                draft.codeStep = 2
            })
        }
        if (lastCount > 0 && count > lastCount) {
            setState(draft => {
                draft.count = lastCount
                draft.codeText = `${String(lastCount)} s后重发`
                draft.codeStep = 2
            })
            return undefined
        }
        if (codeStep === 2) {
            if (count > 0) {
                const codeTimer = setTimeout(() => {
                    setState(draft => {
                        draft.codeText = `${String(count - 1)} s后重发`
                        draft.count = count - 1
                    })
                }, 1000)
                return () => {
                    const dateValue = subtract(60, count)
                    const nowStr = String(Date.now()).slice(0, 10)
                    const now = subtract(Number(nowStr), dateValue)
                    setCachedItem<string>('lastTime', String(now))
                    clearTimeout(codeTimer)
                }
            }
            setState(draft => {
                draft.count = 60
                draft.codeText = `再次获取`
                draft.codeStep = 0
            })
        }
        return undefined
    }, [currentTime, setState, codeStep, count, prevTime])

    const startCodeDown = useCallback(() => {
        setState(draft => {
            draft.codeStep = 1
            draft.disable = false
        })
    }, [setState])

    const errorCodeDown = useCallback(() => {
        setState(draft => {
            draft.disable = false
        })
    }, [setState])

    /**
     * 获取验证码
     */
    const getPhoneCode = useCallback(
        (result: CaptchaResult) => {
            if (codeStep > 0) {
                return
            }
            setState(draft => {
                draft.disable = true
            })
            onGetCodeHandle?.(result, startCodeDown, errorCodeDown)
        },
        [codeStep, errorCodeDown, onGetCodeHandle, setState, startCodeDown]
    )

    useEffect(() => {
        if (!scriptLoaded || !window.initAlicom4) {
            return
        }

        const captchaConfig = {
            captchaId: 'b948d099e0356c596fc0e0196e1e8ea1',
            language: 'zh',
            product: 'bind'
        }

        window.initAlicom4(captchaConfig, captcha => {
            captcha.onSuccess(() => {
                const result = captcha.getValidate()
                if (!result) {
                    return
                }

                getPhoneCode(result)
            })
            window.captchaInstance = captcha
        })
    }, [getPhoneCode, scriptLoaded])

    const beforeSendCode = useCallback(() => {
        if (window.captchaInstance) {
            window.captchaInstance.showCaptcha()
        }
    }, [])

    return useMemo(
        () => (
            <SC.VerificationCodeTextButton disabled={codeStep > 0 || disable} loading={loading} style={style} onClick={beforeSendCode}>
                {codeText}
            </SC.VerificationCodeTextButton>
        ),
        [beforeSendCode, codeStep, codeText, disable, loading, style]
    )
}
